// https://docs.google.com/spreadsheets/d/1GCrdedP2Kn1quBntCrX0AYnAhZWds4M2RYYCRT3bf4g/edit#gid=272291441
// This doesn't include personal narrative permission logic since it was implemented in the component.
import { KaseRoleBasedAction } from '@/constants';
import { KaseStatus, IKase, IUser, Role } from '@/models';

import {
  isAccountAdmin,
  isAdmin,
  isAnyTargetRole,
  isTargetAgencyRole,
  isTargetRole,
} from './service.role';

// Agency-agnostic "view-only RMS user" check for surfaces that are NOT scoped to a
// single kase (e.g. the global Arrests list, Tasks). True when the user holds
// RMS_CASES_VIEW and no edit-granting role anywhere.
export const isRmsCasesViewOnlyUser = (user: IUser | null): boolean => {
  if (!user) return false;
  if (!isTargetRole(user, Role.RMS_CASES_VIEW)) return false;
  const hasEditAccess =
    isAnyTargetRole(user, [
      Role.RMS_CASES_FULL_ACCESS,
      Role.KASE_APPROVER,
      Role.AGENCY_ADMIN,
    ]) || isAccountAdmin(user);
  return !hasEditAccess;
};

const isViewOnlyKaseUser = (user: IUser, kase: IKase): boolean => {
  const agencyId = kase.agency?._id;

  if (!isTargetAgencyRole(user, Role.RMS_CASES_VIEW, agencyId)) {
    return false;
  }

  const primaryOfficerId = kase.primaryOfficer?._id;
  const isPrimaryOfficer =
    !!primaryOfficerId && String(primaryOfficerId) === String(user._id);

  const hasEditAccess =
    isPrimaryOfficer ||
    isTargetAgencyRole(user, Role.RMS_CASES_FULL_ACCESS, agencyId) ||
    isTargetAgencyRole(user, Role.KASE_APPROVER, agencyId) ||
    isAdmin(user, agencyId ?? '');

  return !hasEditAccess;
};

export const canSubmitKase = (user: IUser, kase: IKase): boolean => {
  const agencyId = kase.agency?._id;
  if (!agencyId) return false;
  const isPrimaryOfficer =
    !!kase.primaryOfficer?._id &&
    String(kase.primaryOfficer._id) === String(user._id);
  return (
    isPrimaryOfficer ||
    isTargetAgencyRole(user, Role.KASE_APPROVER, agencyId) ||
    isAdmin(user, agencyId)
  );
};

export const canCreateKase = (user: IUser, agencyId?: string): boolean => {
  if (!agencyId) return false;
  return (
    isAdmin(user, agencyId) ||
    isTargetAgencyRole(user, Role.RMS_CASES_FULL_ACCESS, agencyId) ||
    isTargetAgencyRole(user, Role.KASE_APPROVER, agencyId)
  );
};

export const isReadOnlyKaseRole = (
  _action: KaseRoleBasedAction,
  user: IUser | null,
  kase: IKase | null,
) => {
  if (!user || !kase) {
    return true;
  }

  // View-only users can never edit, regardless of case status.
  if (isViewOnlyKaseUser(user, kase)) {
    return true;
  }

  switch (kase.status) {
    case KaseStatus.OPEN:
      return false;
    case KaseStatus.WAITING_FOR_SUPERVISOR:
    case KaseStatus.REJECTED:
      // Approvers reviewing a case can edit everything, including the main
      // narrative (previously the main narrative was primary-officer-only).
      return !isTargetAgencyRole(user, Role.KASE_APPROVER);
    default:
      return true;
  }
};

// Non-editing actions (Print, Email, Create Task) stay available while a case
// awaits approval and after it is approved or rejected: they don't modify case
// data. Everything else follows the standard read-only rules.
export const canUseNonEditingKaseActions = (
  user: IUser | null,
  kase: IKase | null,
): boolean => {
  if (!user || !kase) return false;
  if (isViewOnlyKaseUser(user, kase)) return false;
  if (
    kase.status === KaseStatus.WAITING_FOR_SUPERVISOR ||
    kase.status === KaseStatus.APPROVED_BY_SUPERVISOR ||
    kase.status === KaseStatus.REJECTED
  )
    return true;
  return !isReadOnlyKaseRole(KaseRoleBasedAction.FILES, user, kase);
};
